import React, { useEffect, useMemo, useState } from 'react';
import authService from '../auth/AuthService';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    TextField,
    Card,
    CardContent,
    CardHeader,
    Container,
    Box,
    Typography,
    Alert,
    CircularProgress,
    Stack
} from '@mui/material';
import PasswordResetModal from './Modal/PasswordResetModal';
import AuthUtil from './Utils/AuthUtil';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from './Common/SnackbarProvider';

const Home = () => {
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const { t } = useTranslation();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [loggingIn, setLoggingIn] = useState(false);
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [sendingResetPassword, setSendingResetPassword] = useState(false);

    const userToken = useSelector((state) => state.correcttrack.userToken);
    const isAuthorized = useMemo(() => AuthUtil.isAuthorized(userToken), [userToken]);

    async function handleSubmit () {
        setLoggingIn(true);
        if (validate()) {
            const result = await authService.login(username, password);
            if (result.data.success) {
                redirectToTaskPage();
            } else {
                setPassword('');
                setLoginError(result.data.message);
            }
        }

        setLoggingIn(false);
    }

    function redirectToTaskPage () {
        navigate('/Tasks', { replace: true });
    }

    function handleEnterKey(event) {
        if(event.key === 'Enter') {
            handleSubmit();
        }
    }

    function validate () {
        if (username === '') {
            setUsernameError(t('login.validation.usernameRequired'));
        } else {
            setUsernameError('');
        }
        if (password === '') {
            setPasswordError(t('login.validation.passwordRequired'));
        } else {
            setPasswordError('');
        }

        if (usernameError || passwordError) {
            setLoginError('');
            return false;
        }

        return true;
    }

    function closeResetModal() {
        setShowResetPassword(false);
    }

    async function handleResetPasswordRequest(username) {
        setSendingResetPassword(true);
        const response = await authService.requestResetPassword(username);

        if(response.success) {
            snackbar.success(response.message);
        } else if(response.message){
            snackbar.error(response.message);
        } else {
            snackbar.error(response);
        }
        setSendingResetPassword(false);
        closeResetModal();
    }

    useEffect(() => {
        if(isAuthorized) {
            navigate('/Dashboard');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f5f5f5',
                padding: 2
            }}
        >
            <Container maxWidth="sm">
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Box className="logo">
                        <Typography variant="h2" component="div" className="title">
                            <em></em>
                        </Typography>
                    </Box>
                </Box>

                <Card elevation={8} sx={{ borderRadius: 2 }}>
                    <CardHeader
                        title={t('login.title')}
                        sx={{
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            textAlign: 'center',
                            '& .MuiCardHeader-title': {
                                fontSize: '1.5rem',
                                fontWeight: 'bold'
                            }
                        }}
                    />
                    <CardContent sx={{ p: 4 }}>
                        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                            <Stack spacing={3}>
                                {loginError && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {loginError}
                                    </Alert>
                                )}

                                <TextField
                                    fullWidth
                                    label={t('userMainSettings.field.username')}
                                    name="username"
                                    autoComplete="username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    onKeyUp={handleEnterKey}
                                    error={!!usernameError}
                                    helperText={usernameError}
                                    variant="outlined"
                                    required
                                />

                                <TextField
                                    fullWidth
                                    label={t('userMainSettings.field.password')}
                                    name="password"
                                    type="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onKeyUp={handleEnterKey}
                                    error={!!passwordError}
                                    helperText={passwordError}
                                    variant="outlined"
                                    required
                                />

                                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={() => setShowResetPassword(true)}
                                        sx={{ flex: 1 }}
                                    >
                                        {t('login.button.resetPassword')}
                                    </Button>

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={loggingIn}
                                        onClick={handleSubmit}
                                        sx={{ flex: 1 }}
                                        startIcon={loggingIn ? <CircularProgress size={20} color="inherit" /> : null}
                                    >
                                        {loggingIn ? t('login.button.loggingIn') : t('login.button.login')}
                                    </Button>
                                </Stack>
                            </Stack>
                        </Box>
                    </CardContent>
                </Card>
            </Container>

            <PasswordResetModal
                open={showResetPassword}
                close={closeResetModal}
                loading={sendingResetPassword}
                handleSubmitReset={handleResetPasswordRequest}
            />
        </Box>
    );
};

export default Home;
